import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/Layout';
import StickyActionHeader from '../../components/StickyActionHeader';
import InfoTip from '../../components/InfoTip';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { 
  Ticket as TicketIcon, 
  Upload,
  Airplane,
  Train,
  Car,
  Calendar,
  Clock,
  FileText,
  Trash,
  SpinnerGap,
  CheckCircle,
  Users,
  PencilSimple,
  ArrowRight,
  Plus,
  X,
  FloppyDisk
} from '@phosphor-icons/react';
import api from '../../services/api';
import { toast } from 'sonner';
import { format } from 'date-fns';
import EventAwareDatePicker from '../../components/EventAwareDatePicker';
import AIPremiumButton from '../../components/AIPremiumGate';
import ManualTravelDetailsDialog from '../../components/ManualTravelDetailsDialog';

export default function MyTickets() {
  const idTypeOptions = ['Aadhaar Card', 'Passport', 'PAN Card', 'Driving License', 'Voter ID', 'Other'];
  const { currentEvent, user } = useAuth();
  const [guestProfile, setGuestProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [manualEntryType, setManualEntryType] = useState(null);
  const [uploadingIdProof, setUploadingIdProof] = useState(false);
  const [deletingIdProofUrl, setDeletingIdProofUrl] = useState(null);
  const [idUploadDialogOpen, setIdUploadDialogOpen] = useState(false);
  const [selectedIdType, setSelectedIdType] = useState('');
  const [customIdType, setCustomIdType] = useState('');
  const [idProofFile, setIdProofFile] = useState(null);
  const [idPreviewDialog, setIdPreviewDialog] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [arrivalDate, setArrivalDate] = useState('');
  const [arrivalTime, setArrivalTime] = useState('');
  const [arrivalMode, setArrivalMode] = useState('flight');
  const [arrivalDetails, setArrivalDetails] = useState('');
  const [departureDate, setDepartureDate] = useState('');
  const [departureTime, setDepartureTime] = useState('');
  const [departureMode, setDepartureMode] = useState('flight');
  const [departureDetails, setDepartureDetails] = useState('');
  const [deletingTicket, setDeletingTicket] = useState(null);
  const [ocrResultDialog, setOcrResultDialog] = useState(null);
  const [eventGuests, setEventGuests] = useState([]);
  
  // Edit ticket data state
  const [editTicketDialog, setEditTicketDialog] = useState(null);
  const [editTicketData, setEditTicketData] = useState({
    mode: '',
    date: '',
    time: '',
    details: '',
    passenger_names: []
  });
  const [savingTicketEdit, setSavingTicketEdit] = useState(false);
  const [newPassengerName, setNewPassengerName] = useState('');

  const handleArrivalDateChange = (date) => {
    setArrivalDate(date);
    if (date && departureDate && departureDate < date) {
      setDepartureDate('');
    }
  };

  const handleDepartureDateChange = (date) => {
    if (date && arrivalDate && date < arrivalDate) return;
    setDepartureDate(date);
  };

  // Phone-group awareness — when the current user is a primary in a phone
  // group with siblings, they can upload IDs / tickets on behalf of others.
  const [phoneGroup, setPhoneGroup] = useState([]);
  const [primaryGuestId, setPrimaryGuestId] = useState(null);
  // Single-select for ID uploads (default = primary guest = self)
  const [idTargetGuestId, setIdTargetGuestId] = useState(null);

  const fetchGuestProfile = useCallback(async () => {
    if (!currentEvent?.id) return;
    try {
      const [response, groupRes] = await Promise.all([
        api.get(`/events/${currentEvent.id}/guests`),
        api.get(`/events/${currentEvent.id}/my-guest-group`).catch(() => ({ data: {} })),
      ]);
      const guests = response.data;
      setEventGuests(guests);

      const group = groupRes.data?.group || [];
      const primaryId = groupRes.data?.primary_guest_id || null;
      setPhoneGroup(group);
      setPrimaryGuestId(primaryId);
      // Default ID upload target = primary (i.e. self) if known.
      setIdTargetGuestId(primaryId);

      // Prefer the primary guest record, else fall back to the legacy
      // user_id match so returning users don't regress.
      const myProfile =
        group.find((g) => g.id === primaryId) || guests.find((g) => g.user_id === user?.id);
      if (myProfile) {
        setGuestProfile(myProfile);
        const info = myProfile.arrival_info || {};
        setArrivalDate(info.arrival_date ? info.arrival_date.split('T')[0] : '');
        setArrivalTime(info.arrival_time || '');
        setArrivalMode(info.arrival_mode || 'flight');
        setArrivalDetails(info.arrival_details || '');
        setDepartureDate(info.departure_date ? info.departure_date.split('T')[0] : '');
        setDepartureTime(info.departure_time || '');
        setDepartureMode(info.departure_mode || 'flight');
        setDepartureDetails(info.departure_details || '');
      }
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    } finally {
      setLoading(false);
    }
  }, [currentEvent?.id, user?.id]);

  useEffect(() => {
    fetchGuestProfile();
  }, [fetchGuestProfile]);

  const handleSaveArrival = async () => {
    if (!guestProfile) return;
    if (arrivalDate && departureDate && departureDate < arrivalDate) {
      toast.error('Departure date cannot be earlier than arrival date');
      return;
    }
    try {
      await api.put(`/events/${currentEvent.id}/guests/${guestProfile.id}`, {
        arrival_info: {
          arrival_date: arrivalDate ? new Date(arrivalDate).toISOString() : null,
          arrival_time: arrivalTime,
          arrival_mode: arrivalMode,
          arrival_details: arrivalDetails,
          departure_date: departureDate ? new Date(departureDate).toISOString() : null,
          departure_time: departureTime,
          departure_mode: departureMode,
          departure_details: departureDetails,
          ticket_uploads: guestProfile.arrival_info?.ticket_uploads || [],
          arrival_tickets: guestProfile.arrival_info?.arrival_tickets || [],
          departure_tickets: guestProfile.arrival_info?.departure_tickets || []
        }
      });
      toast.success('Travel details updated');
      setEditMode(false);
      fetchGuestProfile();
    } catch (error) {
      toast.error('Failed to update details');
    }
  };

  const handleDeleteTicket = async (ticketId) => {
    if (!guestProfile) return;
    
    setDeletingTicket(ticketId);
    try {
      await api.delete(`/events/${currentEvent.id}/guests/${guestProfile.id}/tickets/${ticketId}`);
      toast.success('Ticket deleted');
      fetchGuestProfile();
    } catch (error) {
      toast.error('Failed to delete ticket');
    } finally {
      setDeletingTicket(null);
    }
  };

  const resolveUploadedFileUrl = (url) => (
    url?.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url
  );

  const getIdUploads = (idInfo) => (
    (idInfo?.id_uploads || []).map((upload, index) => {
      if (typeof upload === 'string') {
        return {
          url: upload,
          id_type: idInfo?.id_type || 'ID Proof',
          uploaded_at: null,
          key: `${upload}-${index}`
        };
      }

      return {
        url: upload.url,
        id_type: upload.id_type || idInfo?.id_type || 'ID Proof',
        uploaded_at: upload.uploaded_at || null,
        key: `${upload.url}-${index}`
      };
    })
  );

  const isPdfFile = (url) => url?.toLowerCase().split('?')[0].endsWith('.pdf');

  const openIdPreview = (url, label) => {
    setIdPreviewDialog({
      url: resolveUploadedFileUrl(url),
      label
    });
  };

  const resetIdUploadForm = () => {
    setSelectedIdType('');
    setCustomIdType('');
    setIdProofFile(null);
  };

  const handleUploadIdProof = async () => {
    const targetId = idTargetGuestId || guestProfile?.id;
    if (!idProofFile || !targetId) return;

    const resolvedIdType = selectedIdType === 'Other' ? customIdType.trim() : selectedIdType;
    if (!resolvedIdType) {
      toast.error('Please select an ID type');
      return;
    }

    setUploadingIdProof(true);
    const formData = new FormData();
    formData.append('file', idProofFile);
    formData.append('id_type', selectedIdType);
    if (selectedIdType === 'Other') {
      formData.append('custom_id_type', customIdType.trim());
    }

    try {
      await api.post(
        `/events/${currentEvent.id}/guests/${targetId}/id-proof`,
        formData
      );
      toast.success('ID proof uploaded successfully');
      setIdUploadDialogOpen(false);
      resetIdUploadForm();
      fetchGuestProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload ID proof');
    } finally {
      setUploadingIdProof(false);
    }
  };

  const handleDeleteIdProof = async (url) => {
    if (!guestProfile) return;

    setDeletingIdProofUrl(url);
    try {
      await api.delete(`/events/${currentEvent.id}/guests/${guestProfile.id}/id-proof`, {
        params: { url }
      });
      toast.success('ID proof deleted');
      fetchGuestProfile();
      if (idPreviewDialog?.url === resolveUploadedFileUrl(url)) {
        setIdPreviewDialog(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete ID proof');
    } finally {
      setDeletingIdProofUrl(null);
    }
  };

  const applyOcrData = (ticketType) => {
    if (!ocrResultDialog?.extractedData) return;
    
    const data = ocrResultDialog.extractedData;
    
    if (ticketType === 'arrival') {
      if (data.date) handleArrivalDateChange(data.date);
      if (data.time) setArrivalTime(data.time);
      if (data.mode) setArrivalMode(data.mode);
      if (data.details) setArrivalDetails(data.details);
    } else {
      if (data.date) handleDepartureDateChange(data.date);
      if (data.time) setDepartureTime(data.time);
      if (data.mode) setDepartureMode(data.mode);
      if (data.details) setDepartureDetails(data.details);
    }
    
    setEditMode(true);
    setOcrResultDialog(null);
    toast.success('Details populated from ticket. Click Save to confirm.');
  };

  const getModeIcon = (mode) => {
    if (mode === 'flight') return Airplane;
    if (mode === 'train') return Train;
    if (mode === 'car') return Car;
    return TicketIcon;
  };

  const getGuestName = (guestId) => {
    const guest = eventGuests.find(g => g.id === guestId);
    return guest?.name || 'Unknown Guest';
  };

  // Open edit dialog for a ticket
  const openEditTicketDialog = (ticket, ticketType) => {
    const extractedData = ticket.extracted_data || {};
    setEditTicketData({
      mode: extractedData.mode || '',
      date: extractedData.date || '',
      time: extractedData.time || '',
      details: extractedData.details || '',
      passenger_names: extractedData.passenger_names || []
    });
    setEditTicketDialog({ ticket, ticketType });
    setNewPassengerName('');
  };

  // Save edited ticket data
  const handleSaveTicketEdit = async () => {
    if (!editTicketDialog || !guestProfile) return;
    
    setSavingTicketEdit(true);
    try {
      await api.put(
        `/events/${currentEvent.id}/guests/${guestProfile.id}/tickets/${editTicketDialog.ticket.id}`,
        editTicketData
      );
      toast.success('Ticket data updated');
      setEditTicketDialog(null);
      fetchGuestProfile();
    } catch (error) {
      toast.error('Failed to update ticket data');
    } finally {
      setSavingTicketEdit(false);
    }
  };

  // Add passenger name to the list
  const addPassengerName = () => {
    if (newPassengerName.trim()) {
      setEditTicketData(prev => ({
        ...prev,
        passenger_names: [...prev.passenger_names, newPassengerName.trim()]
      }));
      setNewPassengerName('');
    }
  };

  // Remove passenger name from the list
  const removePassengerName = (index) => {
    setEditTicketData(prev => ({
      ...prev,
      passenger_names: prev.passenger_names.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-wedding-sage/20 rounded w-1/4"></div>
          <div className="h-64 bg-wedding-sage/10 rounded"></div>
        </div>
      </DashboardLayout>
    );
  }

  const ArrivalIcon = getModeIcon(arrivalMode);
  const DepartureIcon = getModeIcon(departureMode);
  const arrivalTickets = guestProfile?.arrival_info?.arrival_tickets || [];
  const departureTickets = guestProfile?.arrival_info?.departure_tickets || [];
  const idUploads = getIdUploads(guestProfile?.id_info);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <StickyActionHeader desktopOffset="guest-menu">
          <div className="flex items-center gap-1">
            <h1 className="font-serif text-2xl font-bold text-wedding-charcoal" data-testid="tickets-title">My Tickets & ID</h1>
            <InfoTip content="Manage your travel details and ID proofs." label="About tickets and ID" />
          </div>
          <div className="flex w-full flex-wrap gap-2 sm:w-auto sm:justify-end" data-header-action-group>
            {!editMode ? (
              <Button
                variant="outline"
                onClick={() => setEditMode(true)}
                aria-label="Edit travel details"
                title="Edit travel details"
                data-header-action
                data-testid="edit-travel-btn"
              >
                <PencilSimple className="mr-2 h-4 w-4" />
                Edit Details
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={() => setEditMode(false)}
                  aria-label="Cancel editing"
                  title="Cancel editing"
                  data-header-action
                >
                  <X className="mr-2 h-4 w-4" />
                  Cancel
                </Button>
                <Button
                  className="bg-wedding-sage hover:bg-wedding-sage/90"
                  onClick={handleSaveArrival}
                  aria-label="Save travel details"
                  title="Save travel details"
                  data-header-action
                  data-testid="save-travel-btn"
                >
                  <FloppyDisk className="mr-2 h-4 w-4" />
                  Save
                </Button>
              </>
            )}
          </div>
        </StickyActionHeader>

        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <FileText className="w-5 h-5 text-wedding-sage" />
                ID Proofs
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIdUploadDialogOpen(true)}
                  disabled={uploadingIdProof}
                  data-testid="upload-id-proof-btn"
                >
                  {uploadingIdProof ? (
                    <SpinnerGap className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  {uploadingIdProof ? 'Uploading...' : 'Upload ID'}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {idUploads.length > 0 ? (
              <div className="space-y-3">
                {idUploads.map((upload) => (
                  <div
                    key={upload.key}
                    className="flex items-center gap-3 rounded-lg border p-3 bg-muted/30"
                  >
                    <button
                      type="button"
                      onClick={() => openIdPreview(upload.url, upload.id_type || 'ID Proof')}
                      className="flex flex-1 items-center gap-3 min-w-0 text-left hover:text-wedding-sage transition-colors"
                    >
                      <FileText className="w-5 h-5 text-wedding-sage" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{upload.id_type || 'ID Proof'}</p>
                      </div>
                    </button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteIdProof(upload.url)}
                      disabled={deletingIdProofUrl === upload.url}
                      className="text-muted-foreground hover:text-red-600"
                    >
                      {deletingIdProofUrl === upload.url ? (
                        <SpinnerGap className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground">No ID proofs uploaded yet.</p>
            )}
          </CardContent>
        </Card>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Arrival Card */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <ArrivalIcon className="w-5 h-5 text-green-600" />
                  Arrival
                </CardTitle>
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <AIPremiumButton
                    featureName="ticket reading"
                    variant="outline" 
                    size="sm" 
                    onManualEntry={() => setManualEntryType('arrival')}
                    data-testid="upload-arrival-ticket-btn"
                  >
                    Upload Ticket
                  </AIPremiumButton>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {editMode ? (
                <div className="space-y-4">
                  <div>
                    <Label>Mode of Travel</Label>
                    <Select value={arrivalMode} onValueChange={setArrivalMode}>
                      <SelectTrigger data-testid="arrival-mode-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="flight">Flight</SelectItem>
                        <SelectItem value="train">Train</SelectItem>
                        <SelectItem value="car">Car</SelectItem>
                        <SelectItem value="bus">Bus</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <EventAwareDatePicker
                        label="Date"
                        value={arrivalDate}
                        onChange={handleArrivalDateChange}
                        eventStartDate={currentEvent?.start_date}
                        eventEndDate={currentEvent?.end_date}
                        maxDate={departureDate}
                        data-testid="arrival-date-input"
                      />
                    </div>
                    <div>
                      <Label>Time</Label>
                      <Input type="time" value={arrivalTime} onChange={(e) => setArrivalTime(e.target.value)} data-testid="arrival-time-input" />
                    </div>
                  </div>
                  <div>
                    <Label>Details</Label>
                    <Input value={arrivalDetails} onChange={(e) => setArrivalDetails(e.target.value)} placeholder="e.g., AI 101" data-testid="arrival-details-input" />
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {arrivalDate ? (
                    <>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-muted-foreground" />
                        <span>{format(new Date(arrivalDate), 'EEEE, MMMM d, yyyy')}</span>
                      </div>
                      {arrivalTime && (
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-muted-foreground" />
                          <span>{arrivalTime}</span>
                        </div>
                      )}
                      <Badge className="bg-green-100 text-green-700 capitalize">{arrivalMode}</Badge>
                      {arrivalDetails && <p className="text-sm text-muted-foreground">{arrivalDetails}</p>}
                    </>
                  ) : (
                    <p className="text-muted-foreground">No arrival details added yet.</p>
                  )}
                </div>
              )}

              {/* Arrival Tickets List - With Passenger Info */}
              {arrivalTickets.length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Uploaded Tickets ({arrivalTickets.length})
                  </h4>
                  <div className="space-y-3">
                    {arrivalTickets.map((ticket) => (
                      <div key={ticket.id} className="border rounded-lg p-3 bg-muted/30">
                        <div className="flex items-center justify-between">
                          <a 
                            href={ticket.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex items-center gap-3 flex-1 hover:text-wedding-sage transition-colors"
                          >
                            <FileText className="w-6 h-6 text-wedding-sage" />
                            <div>
                              <span className="text-sm font-medium block">{ticket.filename || 'Ticket'}</span>
                              <span className="text-xs text-muted-foreground">
                                {ticket.uploaded_at ? format(new Date(ticket.uploaded_at), 'MMM d, yyyy h:mm a') : ''}
                              </span>
                            </div>
                          </a>
                          <div className="flex items-center gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => openEditTicketDialog(ticket, 'arrival')}
                              className="text-muted-foreground hover:text-wedding-sage"
                              title="Edit ticket data"
                            >
                              <PencilSimple className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleDeleteTicket(ticket.id)}
                              disabled={deletingTicket === ticket.id}
                              className="text-muted-foreground hover:text-red-600"
                              data-testid={`delete-ticket-${ticket.id}`}
                            >
                              {deletingTicket === ticket.id ? (
                                <SpinnerGap className="w-4 h-4 animate-spin" />
                              ) : (
                                <Trash className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                        </div>
                        
                        {/* Show extracted data summary */}
                        {ticket.extracted_data && (ticket.extracted_data.mode || ticket.extracted_data.details) && (
                          <div className="mt-2 pt-2 border-t border-dashed text-xs text-muted-foreground">
                            <div className="flex flex-wrap gap-x-4 gap-y-1">
                              {ticket.extracted_data.mode && (
                                <span className="capitalize">{ticket.extracted_data.mode}</span>
                              )}
                              {ticket.extracted_data.date && (
                                <span>{ticket.extracted_data.date}</span>
                              )}
                              {ticket.extracted_data.time && (
                                <span>{ticket.extracted_data.time}</span>
                              )}
                              {ticket.extracted_data.details && (
                                <span>{ticket.extracted_data.details}</span>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Show passengers */}
                        {ticket.extracted_data?.passenger_names?.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-dashed">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                Passengers:
                              </span>
                              {ticket.extracted_data.passenger_names.map((name, idx) => (
                                <Badge key={idx} variant="secondary" className="text-xs py-0">
                                  {name}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Show accompanying guests */}
                        {ticket.accompanying_guest_ids?.length > 0 && (
                          <div className="mt-2 bg-green-50 rounded p-2">
                            <span className="text-xs text-green-700 flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" />
                              Accompanying guests: {ticket.accompanying_guest_ids.map(id => getGuestName(id)).join(', ')}
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Departure Card */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <DepartureIcon className="w-5 h-5 text-red-600" />
                  Departure
                </CardTitle>
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <AIPremiumButton
                    featureName="ticket reading"
                    variant="outline" 
                    size="sm" 
                    onManualEntry={() => setManualEntryType('departure')}
                    data-testid="upload-departure-ticket-btn"
                  >
                    Upload Ticket
                  </AIPremiumButton>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {editMode ? (
                <div className="space-y-4">
                  <div>
                    <Label>Mode of Travel</Label>
                    <Select value={departureMode} onValueChange={setDepartureMode}>
                      <SelectTrigger data-testid="departure-mode-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="flight">Flight</SelectItem>
                        <SelectItem value="train">Train</SelectItem>
                        <SelectItem value="car">Car</SelectItem>
                        <SelectItem value="bus">Bus</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <EventAwareDatePicker
                        label="Date"
                        value={departureDate}
                        onChange={handleDepartureDateChange}
                        eventStartDate={currentEvent?.start_date}
                        eventEndDate={currentEvent?.end_date}
                        defaultDate={currentEvent?.end_date}
                        minDate={arrivalDate}
                        data-testid="departure-date-input"
                      />
                    </div>
                    <div>
                      <Label>Time</Label>
                      <Input type="time" value={departureTime} onChange={(e) => setDepartureTime(e.target.value)} data-testid="departure-time-input" />
                    </div>
                  </div>
                  <div>
                    <Label>Details</Label>
                    <Input value={departureDetails} onChange={(e) => setDepartureDetails(e.target.value)} placeholder="e.g., AI 102" data-testid="departure-details-input" />
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {departureDate ? (
                    <>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-muted-foreground" />
                        <span>{format(new Date(departureDate), 'EEEE, MMMM d, yyyy')}</span>
                      </div>
                      {departureTime && (
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-muted-foreground" />
                          <span>{departureTime}</span>
                        </div>
                      )}
                      <Badge className="bg-red-100 text-red-700 capitalize">{departureMode}</Badge>
                      {departureDetails && <p className="text-sm text-muted-foreground">{departureDetails}</p>}
                    </>
                  ) : (
                    <p className="text-muted-foreground">No departure details added yet.</p>
                  )}
                </div>
              )}

              {/* Departure Tickets List - With Passenger Info */}
              {departureTickets.length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Uploaded Tickets ({departureTickets.length})
                  </h4>
                  <div className="space-y-3">
                    {departureTickets.map((ticket) => (
                      <div key={ticket.id} className="border rounded-lg p-3 bg-muted/30">
                        <div className="flex items-center justify-between">
                          <a 
                            href={ticket.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex items-center gap-3 flex-1 hover:text-wedding-sage transition-colors"
                          >
                            <FileText className="w-6 h-6 text-wedding-sage" />
                            <div>
                              <span className="text-sm font-medium block">{ticket.filename || 'Ticket'}</span>
                              <span className="text-xs text-muted-foreground">
                                {ticket.uploaded_at ? format(new Date(ticket.uploaded_at), 'MMM d, yyyy h:mm a') : ''}
                              </span>
                            </div>
                          </a>
                          <div className="flex items-center gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => openEditTicketDialog(ticket, 'departure')}
                              className="text-muted-foreground hover:text-wedding-sage"
                              title="Edit ticket data"
                            >
                              <PencilSimple className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleDeleteTicket(ticket.id)}
                              disabled={deletingTicket === ticket.id}
                              className="text-muted-foreground hover:text-red-600"
                              data-testid={`delete-ticket-${ticket.id}`}
                            >
                              {deletingTicket === ticket.id ? (
                                <SpinnerGap className="w-4 h-4 animate-spin" />
                              ) : (
                                <Trash className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                        </div>
                        
                        {/* Show extracted data summary */}
                        {ticket.extracted_data && (ticket.extracted_data.mode || ticket.extracted_data.details) && (
                          <div className="mt-2 pt-2 border-t border-dashed text-xs text-muted-foreground">
                            <div className="flex flex-wrap gap-x-4 gap-y-1">
                              {ticket.extracted_data.mode && (
                                <span className="capitalize">{ticket.extracted_data.mode}</span>
                              )}
                              {ticket.extracted_data.date && (
                                <span>{ticket.extracted_data.date}</span>
                              )}
                              {ticket.extracted_data.time && (
                                <span>{ticket.extracted_data.time}</span>
                              )}
                              {ticket.extracted_data.details && (
                                <span>{ticket.extracted_data.details}</span>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Show passengers */}
                        {ticket.extracted_data?.passenger_names?.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-dashed">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                Passengers:
                              </span>
                              {ticket.extracted_data.passenger_names.map((name, idx) => (
                                <Badge key={idx} variant="secondary" className="text-xs py-0">
                                  {name}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Show accompanying guests */}
                        {ticket.accompanying_guest_ids?.length > 0 && (
                          <div className="mt-2 bg-green-50 rounded p-2">
                            <span className="text-xs text-green-700 flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" />
                              Accompanying guests: {ticket.accompanying_guest_ids.map(id => getGuestName(id)).join(', ')}
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <ManualTravelDetailsDialog
          open={Boolean(manualEntryType)}
          onOpenChange={(open) => { if (!open) setManualEntryType(null); }}
          eventId={currentEvent?.id}
          guest={guestProfile}
          ticketType={manualEntryType || 'arrival'}
          onSaved={fetchGuestProfile}
        />

        {/* OCR Result Dialog */}
        <Dialog open={!!ocrResultDialog} onOpenChange={() => setOcrResultDialog(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                Ticket Details Extracted
              </DialogTitle>
              <DialogDescription>
                We&apos;ve extracted the following details from your {ocrResultDialog?.ticketType} ticket.
              </DialogDescription>
            </DialogHeader>
            
            {ocrResultDialog?.extractedData && (
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {ocrResultDialog.extractedData.mode && (
                    <div>
                      <span className="text-muted-foreground">Mode:</span>
                      <p className="font-medium capitalize">{ocrResultDialog.extractedData.mode}</p>
                    </div>
                  )}
                  {ocrResultDialog.extractedData.date && (
                    <div>
                      <span className="text-muted-foreground">Date:</span>
                      <p className="font-medium">{ocrResultDialog.extractedData.date}</p>
                    </div>
                  )}
                  {ocrResultDialog.extractedData.time && (
                    <div>
                      <span className="text-muted-foreground">Time:</span>
                      <p className="font-medium">{ocrResultDialog.extractedData.time}</p>
                    </div>
                  )}
                  {ocrResultDialog.extractedData.details && (
                    <div className="col-span-2">
                      <span className="text-muted-foreground">Details:</span>
                      <p className="font-medium">{ocrResultDialog.extractedData.details}</p>
                    </div>
                  )}
                </div>

                {ocrResultDialog.extractedData.passenger_names?.length > 0 && (
                  <div className="pt-2 border-t">
                    <span className="text-sm text-muted-foreground flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      Passengers found:
                    </span>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {ocrResultDialog.extractedData.passenger_names.map((name, idx) => (
                        <Badge key={idx} variant="secondary">{name}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {ocrResultDialog.accompanyingGuestIds?.length > 0 && (
                  <div className="pt-2 border-t bg-green-50 -mx-6 px-6 py-3">
                    <span className="text-sm text-green-700 flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" />
                      Matched event guests (will be tagged as accompanying):
                    </span>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {ocrResultDialog.accompanyingGuestIds.map((guestId) => (
                        <Badge key={guestId} className="bg-green-100 text-green-700">
                          {getGuestName(guestId)}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  <Button 
                    onClick={() => applyOcrData(ocrResultDialog.ticketType)}
                    className="flex-1 bg-wedding-sage hover:bg-wedding-sage/90"
                  >
                    Apply Details
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => setOcrResultDialog(null)}
                  >
                    Skip
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={idUploadDialogOpen} onOpenChange={(open) => {
          setIdUploadDialogOpen(open);
          if (!open) resetIdUploadForm();
        }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Upload ID Proof</DialogTitle>
              <DialogDescription>Select the ID type and upload the file.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {phoneGroup.length > 1 && (
                <div>
                  <Label>Uploading ID for</Label>
                  <Select
                    value={idTargetGuestId || guestProfile?.id || ''}
                    onValueChange={setIdTargetGuestId}
                  >
                    <SelectTrigger data-testid="id-target-guest-select">
                      <SelectValue placeholder="Select guest" />
                    </SelectTrigger>
                    <SelectContent>
                      {phoneGroup.map((g) => (
                        <SelectItem key={g.id} value={g.id}>
                          {g.name}{g.id === primaryGuestId ? ' (You)' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    You share a phone number with other guests. Pick whose ID this is.
                  </p>
                </div>
              )}
              <div>
                <Label>ID Type</Label>
                <Select value={selectedIdType} onValueChange={setSelectedIdType}>
                  <SelectTrigger data-testid="id-type-select">
                    <SelectValue placeholder="Select ID type" />
                  </SelectTrigger>
                  <SelectContent>
                    {idTypeOptions.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {selectedIdType === 'Other' && (
                <div>
                  <Label htmlFor="custom-id-type">Custom ID Type</Label>
                  <Input
                    id="custom-id-type"
                    value={customIdType}
                    onChange={(e) => setCustomIdType(e.target.value)}
                    placeholder="Enter ID type"
                    data-testid="custom-id-type-input"
                  />
                </div>
              )}
              <div>
                <Label htmlFor="id-proof-file">ID File</Label>
                <Input
                  id="id-proof-file"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                  onChange={(e) => setIdProofFile(e.target.files?.[0] || null)}
                  data-testid="id-proof-file-input"
                />
                <p className="mt-2 text-xs text-muted-foreground">
                  Your ID will not be shared or permanently stored. It is kept securely only for this event and automatically deleted after the event ends.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIdUploadDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleUploadIdProof}
                disabled={uploadingIdProof || !idProofFile}
                className="bg-wedding-sage hover:bg-wedding-sage/90"
              >
                {uploadingIdProof ? 'Uploading...' : 'Upload ID'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!idPreviewDialog} onOpenChange={() => setIdPreviewDialog(null)}>
          <DialogContent className="sm:max-w-3xl">
            <DialogHeader>
              <DialogTitle>{idPreviewDialog?.label || 'ID Preview'}</DialogTitle>
            </DialogHeader>
            {idPreviewDialog?.url && (
              <div className="py-2">
                {isPdfFile(idPreviewDialog.url) ? (
                  <iframe
                    src={idPreviewDialog.url}
                    title={idPreviewDialog.label || 'ID Preview'}
                    className="w-full h-[70vh] rounded border"
                  />
                ) : (
                  <img
                    src={idPreviewDialog.url}
                    alt={idPreviewDialog.label || 'ID Preview'}
                    className="w-full max-h-[70vh] object-contain rounded border"
                  />
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Ticket Data Dialog */}
        <Dialog open={!!editTicketDialog} onOpenChange={() => setEditTicketDialog(null)}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <PencilSimple className="w-5 h-5 text-wedding-sage" />
                Edit Ticket Data
              </DialogTitle>
              <DialogDescription>
                Correct or update the extracted information from this {editTicketDialog?.ticketType} ticket.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Mode of Travel</Label>
                  <Select 
                    value={editTicketData.mode} 
                    onValueChange={(value) => setEditTicketData(prev => ({ ...prev, mode: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select mode" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="flight">Flight</SelectItem>
                      <SelectItem value="train">Train</SelectItem>
                      <SelectItem value="bus">Bus</SelectItem>
                      <SelectItem value="car">Car</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <EventAwareDatePicker
                    label="Date"
                    value={editTicketData.date}
                    onChange={(date) => setEditTicketData(prev => ({ ...prev, date }))}
                    eventStartDate={currentEvent?.start_date}
                    eventEndDate={currentEvent?.end_date}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Time</Label>
                  <Input 
                    type="time" 
                    value={editTicketData.time} 
                    onChange={(e) => setEditTicketData(prev => ({ ...prev, time: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Details (Flight/Train No.)</Label>
                  <Input 
                    value={editTicketData.details} 
                    onChange={(e) => setEditTicketData(prev => ({ ...prev, details: e.target.value }))}
                    placeholder="e.g., AI 101"
                  />
                </div>
              </div>
              
              <div>
                <Label>Passengers</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {editTicketData.passenger_names.map((name, idx) => (
                    <Badge key={idx} variant="secondary" className="flex items-center gap-1 pr-1">
                      {name}
                      <button 
                        onClick={() => removePassengerName(idx)}
                        className="ml-1 hover:text-red-600 rounded-full"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2 mt-2">
                  <Input 
                    value={newPassengerName}
                    onChange={(e) => setNewPassengerName(e.target.value)}
                    placeholder="Add passenger name"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addPassengerName())}
                  />
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={addPassengerName}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Passengers matching event guests will be tagged as accompanying.
                </p>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditTicketDialog(null)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSaveTicketEdit}
                disabled={savingTicketEdit}
                className="bg-wedding-sage hover:bg-wedding-sage/90"
              >
                {savingTicketEdit ? (
                  <>
                    <SpinnerGap className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
